import React, { useState, useMemo } from 'react';
import { DownloadResultsButton } from './DownloadResultsButton';
import { ResultFilterBar } from './ResultFilterBar';

interface ResultsVisualizationProps {
  data: any[];
  columns: string[];
  onExport?: (format: 'csv' | 'json') => void;
  queryId?: string;
  sqlQuery?: string;
}

/**
 * Converts any value to a display string.
 * For numbers, avoids JavaScript's scientific notation (e.g. 1e+22 → full digits).
 * Non-finite values (Infinity, NaN) fall back to String().
 */
const toDisplayString = (val: any): string => {
  // Unwrap typed DB payloads like { type: 'jsonb', value: '...', null: false }
  if (val && typeof val === 'object' && 'type' in val && 'value' in val) {
    const typed = val as { type?: string; value?: any; null?: boolean };
    if (typed.null === true || typed.value === null || typed.value === undefined) {
      return 'NULL';
    }
    const dbType = String(typed.type || '').toLowerCase();
    if ((dbType === 'json' || dbType === 'jsonb') && typeof typed.value === 'string') {
      try {
        return JSON.stringify(JSON.parse(typed.value));
      } catch {
        return typed.value;
      }
    }
    return toDisplayString(typed.value);
  }

  if (val && typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }

  if (typeof val !== 'number') return String(val);
  if (!isFinite(val)) return String(val);
  const raw = val.toString();
  // Fast path: no scientific notation
  if (!raw.includes('e') && !raw.includes('E')) return raw;
  // Slow path: expand scientific notation.
  // toLocaleString with useGrouping:false and maximumFractionDigits:20
  // expands the number without introducing thousand separators.
  return val.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
};

const resolveEmptyResultMessage = (sqlQuery?: string): string => {
  const normalized = (sqlQuery || '').trim().toLowerCase();
  if (!normalized) return 'No rows returned.';

  if (normalized.startsWith('create table')) return 'Table created successfully.';
  if (normalized.startsWith('alter table')) return 'Table altered successfully.';
  if (normalized.startsWith('drop table')) return 'Table dropped successfully.';
  if (normalized.startsWith('truncate table')) return 'Table truncated successfully.';
  if (normalized.startsWith('create index')) return 'Index created successfully.';
  if (normalized.startsWith('insert into')) return 'Insert completed successfully.';
  if (normalized.startsWith('update ')) return 'Update completed successfully.';
  if (normalized.startsWith('delete from')) return 'Delete completed successfully.';

  return 'No rows returned.';
};

export const ResultsVisualization: React.FC<ResultsVisualizationProps> = ({
  data,
  columns,
  queryId,
  sqlQuery,
}) => {
  const [filters, setFilters] = useState<Record<string, string>>({});

  const columnDefs = useMemo(
    () => columns.map((col) => ({ id: col, label: col })),
    [columns]
  );

  const filteredData = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([, v]) => v && v.trim());
    if (activeFilters.length === 0) return data;
    return data.filter((row) =>
      activeFilters.every(([colId, filterValue]) =>
        toDisplayString(row?.[colId] ?? '').toLowerCase().includes(filterValue.toLowerCase())
      )
    );
  }, [data, filters]);

  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 16 }}>
        <p style={{ margin: 0, fontSize: 13 }}>{resolveEmptyResultMessage(sqlQuery)}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ResultFilterBar
          columns={columnDefs}
          filters={filters}
          onFilterChange={setFilters}
        />
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <DownloadResultsButton data={filteredData} queryId={queryId} sqlQuery={sqlQuery} />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 6, border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 400 }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '7px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    borderRight: '1px solid #e5e7eb',
                    userSelect: 'none',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa',
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col}
                    style={{
                      padding: '5px 12px',
                      color: '#111827',
                      borderRight: '1px solid #e5e7eb',
                    }}
                  >
                    {row?.[col] === null || row?.[col] === undefined ? (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }} title="NULL">NULL</span>
                    ) : (
                      <div
                        title={toDisplayString(row[col])}
                        style={{
                          maxWidth: 240,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {toDisplayString(row[col])}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredData.length < data.length && (
        <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'right' }}>
          Showing {filteredData.length} of {data.length} rows (filtered)
        </div>
      )}
    </div>
  );
};
